import axios, { InternalAxiosRequestConfig } from 'axios';

import {
  AUTH_TOKEN,
  AUTH_TOKEN_REFRESH,
  REFRESH_TOKEN,
  TIMEOUT,
  TOKEN_HEADER,
  X_APP_TYPE_HEADER,
} from '@repo/constants';

const host = import.meta.env.VITE_BACKEND_HOST;

export const $api = axios.create({
  baseURL: host,
  timeout: TIMEOUT,
  validateStatus: (status) => status < 400,
});

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  if (window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login';
  }
};

$api.interceptors.request.use(
  function (config) {
    const authToken = localStorage.getItem(AUTH_TOKEN);

    if (authToken) {
      config.headers.set('Authorization', TOKEN_HEADER + authToken);
    } else {
      config.headers.delete('Authorization');
    }

    config.headers.set(X_APP_TYPE_HEADER, import.meta.env.VITE_APP_TYPE ?? 'management');

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

$api.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const originalRequest = error.config as RetryableConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);

      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.set('Authorization', TOKEN_HEADER + token);
          return $api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        axios
          .post(`${host}${AUTH_TOKEN_REFRESH}`, { refresh: refreshToken })
          .then(({ data }) => {
            localStorage.setItem(AUTH_TOKEN, data.access);
            localStorage.setItem(REFRESH_TOKEN, data.refresh);
            $api.defaults.headers.common['Authorization'] =
              TOKEN_HEADER + data.access;
            processQueue(null, data.access);
            originalRequest.headers.set(
              'Authorization',
              TOKEN_HEADER + data.access,
            );
            resolve($api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            clearAuthAndRedirect();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    if (!error.response) {
      error.message = 'Network Connection Error';
    }

    return Promise.reject(error);
  },
);
